import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Textarea,
} from "@cloudscape-design/components";
import { API_BASE_URL } from "./config";
import MarkdownMessage from "./components/MarkdownMessage";
import { useDocumentTitle } from "./useDocumentTitle";
import {
  AgentFunctionCallItem,
  AgentFunctionCallOutputItem,
  AgentItem,
  AgentMessageItem,
  formatToolOutput,
  messageText,
  toInputItem,
  toolLabel,
} from "./agentChat";

// Custom header required by the BFF for CSRF protection.
// Cross-origin requests with custom headers trigger a CORS preflight,
// which prevents cross-site request forgery.
const SAP_HEADERS: HeadersInit = {
  "x-sap-csrf": "1",
};

const MAX_MESSAGE_CHARS = 2000;

export default function SapDataspherePage() {
  useDocumentTitle("SAP Datasphere");
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chat state. `history` is the full conversation replayed to the stateless agent.
  const [history, setHistory] = useState<AgentItem[]>([]);
  const [input, setInput] = useState("");
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);

  // Handle OAuth error passed via query param from backend redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("error");
    if (oauthError) {
      setError(`SAP authentication failed: ${oauthError}`);
      // Clean the URL so the error doesn't persist on refresh
      params.delete("error");
      const cleanUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, []);

  // Check connection status on mount using the lightweight /session endpoint
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/sap-datasphere/session`, {
          credentials: "include",
          headers: SAP_HEADERS,
        });
        if (res.ok) {
          const body = await res.json();
          setConnected(body.active === true);
        }
      } catch {
        // Not connected — ignore
      }
    };
    checkConnection();
  }, []);


  const handleConnect = () => {
    window.location.href = `${API_BASE_URL}/sap-datasphere/connect`;
  };

  const handleDisconnect = async () => {
    try {
      await fetch(`${API_BASE_URL}/sap-datasphere/disconnect`, {
        method: "POST",
        credentials: "include",
        headers: SAP_HEADERS,
      });
    } catch {
      // Best effort
    }
    setConnected(false);
    setError(null);
    setHistory([]);
    setAgentError(null);
  };

  const handleSend = async () => {
    const question = input.trim();
    if (!question || agentLoading) return;

    const userItem: AgentMessageItem = { type: "message", role: "user", content: question };
    // Send the history including this turn; the agent has no memory of its own.
    const outgoing = [...history, userItem];

    setHistory(outgoing);
    setInput("");
    setAgentError(null);
    setAgentLoading(true);

    try {
      // Step 1: Submit the query — returns immediately with a requestId
      const submitRes = await fetch(`${API_BASE_URL}/sap-datasphere/agent-query`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-sap-csrf": "1",
        },
        body: JSON.stringify({ messages: outgoing }),
      });

      if (!submitRes.ok) {
        if (submitRes.status === 401) {
          setConnected(false);
          setAgentError("SAP session expired. Please reconnect to SAP Datasphere.");
        } else {
          const errBody = await submitRes.json().catch(() => ({}));
          setAgentError(errBody?.error?.message || `Agent request failed (HTTP ${submitRes.status})`);
        }
        return;
      }

      const { requestId } = await submitRes.json();
      if (!requestId) {
        setAgentError("Failed to start agent query — no request ID returned.");
        return;
      }

      // Step 2: Poll for the result every 2 seconds
      const POLL_INTERVAL = 2000;
      const MAX_POLLS = 90; // 90 * 2s = 3 minutes max
      let polls = 0;

      while (polls < MAX_POLLS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
        polls++;

        const pollRes = await fetch(
          `${API_BASE_URL}/sap-datasphere/agent-query/${requestId}`,
          {
            credentials: "include",
            headers: { "x-sap-csrf": "1" },
          }
        );

        if (!pollRes.ok) {
          setAgentError(`Polling failed (HTTP ${pollRes.status})`);
          return;
        }

        const pollData = await pollRes.json();

        if (pollData.status === "processing") {
          continue; // Still working — poll again
        }

        if (pollData.status === "done") {
          const newItems = (Array.isArray(pollData.output) ? pollData.output : [])
            .map(toInputItem)
            .filter((item: AgentItem | null): item is AgentItem => item !== null);

          if (newItems.length === 0) {
            setAgentError("The agent returned an empty response. Try rephrasing your question.");
          } else {
            setHistory([...outgoing, ...newItems]);
          }
          return;
        }

        if (pollData.status === "error") {
          setAgentError(pollData.error || "Agent request failed. Please try again later.");
          return;
        }
      }

      // Exceeded max polls
      setAgentError("Agent is taking too long to respond. Please try again.");
    } catch (err: any) {
      setAgentError(err?.message || "Failed to reach the agent service");
    } finally {
      setAgentLoading(false);
    }
  };

  // Enter sends, Shift+Enter inserts a newline
  const handleKeyDown = (event: CustomEvent<{ key: string; shiftKey: boolean }>) => {
    if (event.detail.key === "Enter" && !event.detail.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const renderItem = (item: AgentItem, index: number) => {
    const key = `item-${index}`;

    if (item.type === "message") {
      const text = messageText(item as AgentMessageItem);
      if (!text.trim()) return null;
      const isUser = item.role === "user";

      return (
        <div
          key={key}
          style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}
        >
          <div style={{ maxWidth: "85%", minWidth: 0 }}>
            <Box
              padding={{ vertical: "xs", horizontal: "s" }}
              color={isUser ? "text-status-info" : "text-body-secondary"}
              fontSize="body-s"
              fontWeight="bold"
            >
              {isUser ? "You" : "Agent"}
            </Box>
            <Box
              padding={{ vertical: "xs", horizontal: "s" }}
              variant="div"
            >
              <div dir="auto">
                {isUser ? <Box variant="p">{text}</Box> : <MarkdownMessage text={text} />}
              </div>
            </Box>
          </div>
        </div>
      );
    }

    if (item.type === "function_call") {
      const call = item as AgentFunctionCallItem;
      // The matching output is rendered inside this section, so it is skipped below.
      const output = history.find(
        (candidate) =>
          candidate.type === "function_call_output" &&
          (candidate as AgentFunctionCallOutputItem).call_id === call.call_id
      ) as AgentFunctionCallOutputItem | undefined;

      return (
        <Box key={key} padding={{ left: "s" }}>
          <ExpandableSection
            headerText={`Used tool: ${toolLabel(call)}`}
            variant="footer"
          >
            <SpaceBetween size="xs">
              <Box variant="small">Arguments</Box>
              <Box variant="code" fontSize="body-s">
                {call.arguments || "{}"}
              </Box>
              {output && (
                <>
                  <Box variant="small">Result</Box>
                  <pre
                    style={{
                      maxHeight: 240,
                      overflow: "auto",
                      fontSize: "0.75rem",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {formatToolOutput(output.output)}
                  </pre>
                </>
              )}
            </SpaceBetween>
          </ExpandableSection>
        </Box>
      );
    }

    // function_call_output is shown within its function_call section
    return null;
  };

  const charCount = input.trim().length;
  const tooLong = charCount > MAX_MESSAGE_CHARS;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="Proof of concept: OAuth2 Token-Mediating Backend flow to SAP Datasphere"
          >
            SAP Datasphere POC
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Box>
            <SpaceBetween direction="horizontal" size="xs">
              <StatusIndicator type={connected ? "success" : "stopped"}>
                {connected ? "Connected to SAP" : "Not connected"}
              </StatusIndicator>
            </SpaceBetween>
          </Box>

          <SpaceBetween direction="horizontal" size="xs">
            {!connected ? (
              <Button variant="primary" onClick={handleConnect}>
                Connect to SAP
              </Button>
            ) : (
              <Button onClick={handleDisconnect}>Disconnect</Button>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {connected && (
        <Container
          header={
            <Header
              variant="h3"
              description="Ask questions about SAP data. The agent remembers this conversation."
              actions={
                <Button
                  onClick={() => {
                    setHistory([]);
                    setAgentError(null);
                  }}
                  disabled={history.length === 0 || agentLoading}
                >
                  New conversation
                </Button>
              }
            >
              Ask the Agent
            </Header>
          }
        >
          <SpaceBetween size="m">
            <div style={{ maxHeight: 480, overflowY: "auto" }}>
              {history.length === 0 && !agentLoading ? (
                <Box textAlign="center" color="text-body-secondary" padding="l">
                  Start by asking something, for example "give me the first 3 rows of purchase
                  order data".
                </Box>
              ) : (
                <SpaceBetween size="xs">
                  {history.map(renderItem)}
                </SpaceBetween>
              )}
            </div>

            {agentLoading && (
              <Box padding={{ horizontal: "s" }}>
                <SpaceBetween direction="horizontal" size="xs">
                  <Spinner />
                  <Box color="text-body-secondary">Agent is working...</Box>
                </SpaceBetween>
              </Box>
            )}

            {agentError && (
              <Alert type="error" dismissible onDismiss={() => setAgentError(null)}>
                {agentError}
              </Alert>
            )}

            <Textarea
              value={input}
              onChange={({ detail }) => setInput(detail.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about SAP data. Enter to send, Shift+Enter for a new line."
              rows={3}
              disabled={agentLoading}
              invalid={tooLong}
            />

            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Button
                variant="primary"
                onClick={handleSend}
                loading={agentLoading}
                disabled={charCount === 0 || tooLong}
              >
                Send
              </Button>
              {tooLong && (
                <Box color="text-status-error" fontSize="body-s">
                  {charCount} / {MAX_MESSAGE_CHARS} characters
                </Box>
              )}
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      )}

    </SpaceBetween>
  );
}
